import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { EmbeddedCtxSource } from "@/data/tb/embedded";

/**
 * Cross-route context carry — the ONE place that decides which URL params
 * survive a navigation. Every navigation affordance (tab links, command
 * palette, g-chords, tiles, back-links, SCADA drill-downs) must route
 * through this so identical-looking navigations can never behave
 * differently (NAV-04, NAV-07, BESS-05, HIST-11, LINK-04, PERS-04).
 *
 * Two param families are carried:
 *  - RANGE_KEYS — the operator's chosen time window ("what you're looking
 *    at"). Pages that honour it restore it via useUrlTimeRange.
 *  - ENV_KEYS — boot/session wiring (live/mock adapter selection, TB
 *    endpoints, auth, theme, debug). Inert on pages that ignore them, but
 *    LOSING them silently degrades a standalone live session to mock on
 *    the next reload (NAV-07 / PERS-10).
 *
 * Deliberately NOT carried: page-local state like `keys` (Historical
 * series), `zone` (Market price), `mode` (one-shot TB landing param) —
 * those belong to their page and are persisted per-page instead.
 */
const RANGE_KEYS = ["start", "end", "gran", "preset"] as const;
const ENV_KEYS = ["live", "mock", "tb", "tbws", "token", "hostOrigin", "theme", "debug", "lang"] as const;

/**
 * Build the search-string suffix ("?start=…" or "") carrying the range +
 * env params present in `current`. `overrides` lets a link redefine or
 * add params (e.g. an alarm link pinning a forensic window): null removes
 * the key from the result; undefined is skipped entirely so conditional
 * spreads (`...(cond ? {preset: "24h"} : {})`) type-check cleanly.
 */
export type CarryOverrides = Record<string, string | null | undefined>;

export function carriedSearch(
  current: URLSearchParams | string = window.location.search,
  overrides?: CarryOverrides,
): string {
  const sp = typeof current === "string" ? new URLSearchParams(current) : current;
  const qs = new URLSearchParams();
  for (const key of [...RANGE_KEYS, ...ENV_KEYS]) {
    const v = sp.get(key);
    if (v) qs.set(key, v);
  }
  if (overrides) {
    for (const [k, v] of Object.entries(overrides)) {
      if (v === undefined) continue;
      if (v === null) qs.delete(k);
      else qs.set(k, v);
    }
  }
  const s = qs.toString();
  return s ? `?${s}` : "";
}

/**
 * `to` prop helper for <Link>: destination path + carried context.
 * Use for static links (tiles, back-links, header links).
 */
export function carryTo(path: string, overrides?: CarryOverrides): string {
  return path + carriedSearch(window.location.search, overrides);
}

/**
 * navigate() that carries context — for the command palette, keyboard
 * chords and programmatic drill-downs. Inside the ThingsBoard iframe all
 * internal navigation uses `replace` so the host browser's Back button
 * keeps navigating ThingsBoard instead of stepping the invisible iframe
 * history (DOCS-10).
 */
export function useNavigateCarrying() {
  const navigate = useNavigate();
  const location = useLocation();
  return useCallback(
    (path: string, overrides?: CarryOverrides) => {
      navigate(path + carriedSearch(location.search, overrides), {
        replace: EmbeddedCtxSource.isEmbedded(),
      });
    },
    [navigate, location.search],
  );
}
